/**
 * voice-v8 — Aufträge ans Eingabefeld: „ins Textfeld", „Umschreiben",
 * „Zurückholen". EIN Weg, der Reihe nach. (26.09.2026, vierte Runde an den
 * Sprechtasten-Befehlen, Tickets 2674/2672)
 *
 * ## Warum
 *
 * In drei Runden bekam jeder dieser Befehle einen EIGENEN Weg mit EIGENER
 * Fehlerbehandlung — und genau dort entstanden jedes Mal neue Verluste:
 * die Mitschrift ins Feld, wenn die Erkennung scheiterte (fehlte ihr das
 * Ende, war es weg); „Schreibe um …" als Text IM Feld, das beim Fehler
 * zurückgesetzt wurde (was man inzwischen getippt hatte, mit); das
 * Verworfene aus der Ablage genommen, BEVOR es erkannt war.
 *
 * Der Senden-Weg ist seit Monaten robust: die ganze Aufnahme hochladen
 * (drei Versuche), erkennen, und scheitert es trotzdem, bleibt die
 * Aufnahme im Speicher und es erscheint „Erneut senden" (app.ts,
 * offerPttRetry). Diese Aufträge nehmen DENSELBEN Weg (ganz-erkennen.ts:
 * dieselbe Route bis zur fertigen Erkennung) und dieselbe Karte — nur der
 * letzte Schritt ist ein anderer: Text ins Feld statt Modellzug, oder der
 * Umschreib-Auftrag.
 *
 * ## Die Regeln
 *
 *   - Scheitert die Erkennung (Netz, 503, gesperrt …), bleibt die Aufnahme,
 *     und `wiederholen` zeigt „Erneut versuchen" — nie die Mitschrift ins
 *     Feld (ein zweiter Versuch hätte sonst verdoppelt).
 *   - Hat der Server nichts gehört, heißt es „Nichts verstanden." — wie beim
 *     Senden, ohne zweiten Versuch mit denselben stummen Bytes.
 *   - Aus einer Ablage geht nichts vor der gelungenen Erkennung (`gelungen`).
 *   - Die Aufträge laufen der Reihe nach, in der Reihenfolge der Befehle —
 *     auch wenn der Rekorder den Ton des ersten später liefert als der des
 *     zweiten: der Platz in der Reihe wird beim BEFEHL genommen.
 *   - Solange einer läuft, ist das Feld gesperrt, mit sichtbarem Hinweis.
 *     Getipptes bleibt: Text kommt hinten dazu; ersetzt wird nur beim
 *     Umschreiben, und nur, wenn das Feld noch der Entwurf ist.
 */

import { istZurueckholen, type EndwortAngabe } from "../../shared/sende-wort.js";
import { erkenneGanzeAufnahme, type GanzErkannt } from "./ganz-erkennen.js";
import type { FeldStand } from "./diktat-cursor.js";

/** Wohin der erkannte Text geht. */
type FeldZiel = "textfeld" | "umschreiben" | "zurueckholen";

export interface FeldAuftrag {
  ziel: FeldZiel;
  /** Die ganze Aufnahme — sie bleibt, bis der Auftrag gelungen ist. */
  ton: Blob;
  mime: string;
  /** Der Befehl, der die Aufnahme beendete — er fällt am Server aus dem Text. */
  endwort: EndwortAngabe | null;
  /** Die Mitschrift — Rückfall AM SERVER, nur beim ersten Versuch (wie beim Senden). */
  live?: string;
  /** Gilt der Auftrag noch? Vor dem letzten Schritt gefragt (Zurückholen: noch nicht geholt). */
  gilt?: () => boolean;
  /** Nach dem Gelingen — erst dann verlässt ein Verworfenes seine Ablage. */
  gelungen?: () => void;
  /** 21.10.2026: Diktat aus dem Feld-Mikro — Wert und Cursor beim Druck; der Text kommt an die Cursor-Stelle (diktat-cursor.ts). */
  beimDruck?: FeldStand;
}

/** Was im gesperrten Feld steht. */
export const ERKENNE_HINWEIS = "Erkenne …";
const UMSCHREIBE_HINWEIS = "Schreibe um …";

/** Die Sätze der Karte „Erneut versuchen". */
export const NICHT_ERKANNT_SATZ = "Nicht erkannt — deine Aufnahme ist gesichert, nichts geht verloren.";
export const UMSCHREIBEN_GESCHEITERT_SATZ =
  "Umschreiben fehlgeschlagen — der Entwurf ist unverändert, deine Aufnahme gesichert.";

/** Das Eingabefeld, wie die Aufträge es sehen. */
interface FeldZugriff {
  lese(): string;
  /** Hinten anhängen — mit `beimDruck` an die Cursor-Stelle. Getipptes bleibt. */
  haengeAn(text: string, beimDruck?: FeldStand): void;
  ersetze(text: string): void;
  /** Sperren mit sichtbarem Hinweis — `null` gibt es frei. */
  sperre(hinweis: string | null): void;
}

type Umgeschrieben = { ok: true; text: string } | { ok: false; grund: string };

interface FeldDeps {
  /** Die Einstellung `sendeWort` — bei jedem Auftrag frisch gelesen. */
  einstellung: () => string;
  feld: FeldZugriff;
  /** „Erneut versuchen" — dieselbe Karte wie beim Senden (app.ts, offerPttRetry). */
  wiederholen: (a: FeldAuftrag, satz: string) => void;
  melde: (text: string, art: "info" | "warning") => void;
  spur: (zeile: string) => void;
  /** Vorgabe: erkenneGanzeAufnahme (der Weg des Sendens ohne Modellzug). */
  erkenne?: (a: FeldAuftrag, einstellung: string) => Promise<GanzErkannt>;
  /** Vorgabe: umschreibeEntwurf (POST /api/text/umschreiben). */
  umschreiben?: (entwurf: string, anweisung: string) => Promise<Umgeschrieben>;
}

interface FeldAuftraege {
  /**
   * Einreihen — der Platz wird JETZT genommen, das Feld sofort gesperrt.
   * `a` darf noch unterwegs sein (der Rekorder liefert den Rest); `null`
   * heißt: nichts zu tun (zu kurz). Das Versprechen erfüllt sich, wenn
   * DIESER Auftrag fertig ist — es wird nie abgelehnt.
   */
  auftrag(a: FeldAuftrag | null | Promise<FeldAuftrag | null>): Promise<void>;
  /** Wie viele Aufträge noch offen sind (laufend und wartend). */
  offen(): number;
}

/** Der Umschreib-Auftrag an den Server. Nie ein Fehler. */
async function umschreibeEntwurf(
  entwurf: string,
  anweisung: string,
  holen: (url: string, init: RequestInit) => Promise<Response> = (u, i) => fetch(u, i),
): Promise<Umgeschrieben> {
  try {
    const r = await holen("/api/text/umschreiben", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: entwurf, anweisung }),
    });
    const j = (await r.json().catch(() => null)) as { ok?: boolean; text?: unknown; error?: unknown } | null;
    if (!r.ok || !j?.ok || typeof j.text !== "string" || !j.text.trim()) {
      return { ok: false, grund: typeof j?.error === "string" ? j.error : `http-${r.status}` };
    }
    return { ok: true, text: j.text };
  } catch {
    return { ok: false, grund: "netz" };
  }
}

export function neueFeldAuftraege(d: FeldDeps): FeldAuftraege {
  let kette: Promise<void> = Promise.resolve();
  let offen = 0;
  const erkenne =
    d.erkenne ??
    ((a: FeldAuftrag, einstellung: string) =>
      erkenneGanzeAufnahme(a.ton, a.mime, a.endwort, einstellung, {}, a.live ?? ""));
  const umschreiben = d.umschreiben ?? ((e: string, w: string) => umschreibeEntwurf(e, w));

  /** Ein Auftrag — vom Erkennen bis zum letzten Schritt. */
  const fuehreAus = async (a: FeldAuftrag): Promise<void> => {
    d.feld.sperre(ERKENNE_HINWEIS);
    const r = await erkenne(a, d.einstellung());
    const text = r.ok ? r.text.trim() : "";
    if (!r.ok && r.grund !== "nichts") {
      d.spur(`feld-auftrag ziel=${a.ziel} erkennen=${r.grund} — erneut versuchen`);
      d.wiederholen(a, NICHT_ERKANNT_SATZ);
      return;
    }
    if (!text) {
      d.spur(`feld-auftrag ziel=${a.ziel} nichts-verstanden`);
      d.melde("Nichts verstanden.", "info");
      return;
    }
    if (a.gilt && !a.gilt()) {
      d.spur(`feld-auftrag ziel=${a.ziel} schon-erledigt`);
      return;
    }
    const entwurf = d.feld.lese().trim();
    if (a.ziel === "umschreiben" && entwurf) {
      d.feld.sperre(UMSCHREIBE_HINWEIS);
      const u = await umschreiben(entwurf, text);
      if (!u.ok) {
        d.spur(`feld-auftrag ziel=umschreiben umschreiben=${u.grund.slice(0, 40)} — erneut versuchen`);
        d.wiederholen(a, UMSCHREIBEN_GESCHEITERT_SATZ);
        return;
      }
      // Nur ersetzen, was umgeschrieben wurde. Hat sich das Feld doch
      // geändert, kommt das Ergebnis dazu — nichts Getipptes geht verloren.
      if (d.feld.lese().trim() === entwurf) d.feld.ersetze(u.text);
      else d.feld.haengeAn(u.text);
    } else {
      // Ohne Entwurf gibt es nichts umzuschreiben — dann wie „ins Textfeld".
      d.feld.haengeAn(text, a.beimDruck);
    }
    d.spur(`feld-auftrag ziel=${a.ziel} ok zeichen=${text.length}`);
    a.gelungen?.();
  };

  return {
    auftrag(eingang) {
      offen++;
      d.feld.sperre(ERKENNE_HINWEIS);
      const lauf = kette.then(async () => {
        let a: FeldAuftrag | null = null;
        try {
          a = await eingang;
          if (a) await fuehreAus(a);
        } catch (err) {
          // Was auch scheitert: die Aufnahme bleibt, der zweite Versuch steht bereit.
          d.spur(`feld-auftrag fehler ${err instanceof Error ? err.message.slice(0, 60) : "?"}`);
          if (a) d.wiederholen(a, NICHT_ERKANNT_SATZ);
        } finally {
          offen--;
          if (offen === 0) d.feld.sperre(null);
        }
      });
      kette = lauf;
      return lauf;
    },
    offen: () => offen,
  };
}

/**
 * Das gesprochene „zurückholen" / „doch nicht": was die GANZE Aufnahme
 * dieses Befehls sagt. (Vierte Runde)
 *
 * Die Karte löst nach 2 s Ruhe aus, wenn die Mitschrift nur „Doch nicht"
 * zeigt. Eine Denkpause MITTEN in „Doch nicht … so schlimm" (oder eine
 * Mitschrift, die hinterherhinkt) sieht genauso aus. Darum wird diese
 * Aufnahme nicht mehr verworfen, sondern ganz erkannt:
 *
 *   zurueckholen  sie ist nur der Befehl — das Verworfene kommt zurück
 *   senden        es steht mehr darin: im Zweifel SENDEN, nie verwerfen
 *   nichts        der Server hörte nichts
 *   wiederholen   die Erkennung scheiterte — die Aufnahme bleibt, „Erneut senden"
 */
type BefehlsAufnahme = "zurueckholen" | "senden" | "nichts" | "wiederholen";

export function zurueckholBefehl(r: GanzErkannt): BefehlsAufnahme {
  if (!r.ok) return r.grund === "nichts" ? "nichts" : "wiederholen";
  const text = r.text.trim();
  if (!text) return "nichts";
  return istZurueckholen(text) ? "zurueckholen" : "senden";
}
